<script lang="ts">
  import {onMount} from 'svelte';
  import type {IncentivePlanStore} from 'Store/deals/incentivePlan';
  import {incentivePlans} from 'Store/deals/incentivePlan';
  import {deal, fetchDeal} from 'Store/ibex/checkouts/investorCheckoutStore';
  import {dollarMask, percentageMask} from 'Utility/formatters/mask';
  import {humanShortDate} from 'Utility/formatters/dates';
  import Verified from 'Components/Common/icons/Verified.svelte';
  import Disclaimer from 'Components/IncentivePlan/Disclaimer.svelte';
  import type {Writable} from 'svelte/store';
  import type {Deal} from 'Utility/investorFlow/entity';

  export let dealId: string | number;
  export let dealStore: Writable<Deal> = deal;
  export let incentivePlanStore: IncentivePlanStore = incentivePlans;

  onMount(() => {
    if (!!dealId && !$incentivePlanStore) {
      incentivePlanStore.load(dealId);
    }

    if (!!dealId && !$dealStore) {
      fetchDeal(String(dealId));
    }
  });

  $: loading = !$incentivePlanStore || !$dealStore;

  $: incentivePercentage = (
    $incentivePlanStore?.effective_tier?.incentive_percentage * 100 ?? 0
  ).toFixed(2);
  $: maskedPercent = percentageMask(incentivePercentage);
  $: endAt = humanShortDate($incentivePlanStore?.effective_tier?.end_at);
  $: header = $dealStore?.setting?.incentive_plan_header;
</script>

{#if $incentivePlanStore?.effective_tier}
  <div
    class="container"
    class:skeleton={loading}
    data-testid="banner-component"
  >
    <div class="header">
      <Verified />
      <span>{header}</span>
    </div>

    <div class="body">
      <div class="prices skeleton-text">
        <div class="final-price">
          {dollarMask(
            $incentivePlanStore?.final_price.amount,
            $dealStore?.currency_symbol
          )}
        </div>
        <div class="full-price">
          {dollarMask(
            $incentivePlanStore?.full_price.amount,
            $dealStore?.currency_symbol
          )}
        </div>
        <div class="security-unit">
          / {$dealStore?.singularize_security_type}
        </div>
      </div>
      <div class="discount-percent skeleton-text">
        {maskedPercent} off
      </div>
    </div>

    <div class="footer">
      <hr />

      <span class="skeleton-text">
        Only until {endAt}
      </span>
    </div>
  </div>

  <Disclaimer {dealId} {incentivePlanStore} />
{/if}

<style lang="scss">
  .container {
    display: flex;
    padding: 1rem;
    flex-direction: column;
    gap: 0.5rem;
    align-self: stretch;
    border-radius: 1rem;
    border: 1px solid #eaf5f4;
    background: #fafcfc;

    & .header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      align-self: stretch;

      & span {
        color: #257a73;
        font-size: 1rem;
        font-weight: 600;
        line-height: 1.5rem;
      }
    }

    & .body {
      display: flex;
      width: 100%;
      align-items: center;
      gap: 0.5rem;

      & .prices {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex: 1 0 0;

        & .final-price {
          color: #344054;
          font-size: 1rem;
          font-weight: 600;
          line-height: 1.5rem;
        }

        & .full-price {
          text-decoration-line: line-through;
          color: #98a2b3;
          font-size: 1rem;
          font-weight: 500;
          line-height: 1.5rem;
        }

        & .security-unit {
          color: #667085;
          font-size: 0.875rem;
          font-weight: 400;
          line-height: 1.25rem;
        }
      }

      & .discount-percent {
        padding: 2px 0.5rem;
        font-size: 0.875rem;
        font-weight: 600;
        line-height: 1.25rem;
        border-radius: 4px;
        background: #eaf5f4;
        color: #257a73;
        text-align: center;
      }
    }

    & .footer {
      color: #667085;
      text-align: left;
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 1.5rem;

      & hr {
        background: #d5ebe9;
        mix-blend-mode: multiply;
      }
    }
  }

  .skeleton {
    & .discount-percent {
      display: none;
    }

    & .body .skeleton-text {
      .final-price,
      .full-price,
      .security-unit {
        color: transparent;
      }
    }
  }

  .skeleton {
    background: #f0f0f0;
    overflow: hidden;
    position: relative;

    & .skeleton-text {
      color: transparent;
      height: 1.2rem;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
    }
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
</style>
